import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  SampleMasterFormModal,
  ServiceMasterViewModal,
  ConfirmDialog,
  ModuleHeader
} from 'components/modals';
import type {
  ActionButton,
  ViewModeOption,
  ServiceMasterRecord
} from 'components/modals';
import { ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect, SyutSearchGrid, MinimizedModalStrip } from 'components/ui';
import notificationService from '../../../../services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useSampleServices } from 'hooks/api/useSampleServices';
import type { SampleServiceSearchRequest } from 'api/types';

/**
 * ============================================================================
 * PRODUCT MASTER SCREEN v2.0 - PRODUCTION READY
 * ============================================================================
 *
 * NEW FEATURES IN v2.0:
 * ✅ Column Visibility Control (with default/optional columns)
 * ✅ Grid View with product cards
 * ✅ Calendar View (disabled/inactive)
 * ✅ Multiple BU/Entity selection in filters
 * ✅ Searchable Product Category dropdown
 * ✅ Searchable UOM dropdown
 * ✅ Similar product name suggestions
 * ✅ Enhanced validation rules (Entity→Enterprise, single tax at entity)
 * ✅ All previous features maintained
 * ============================================================================
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface ProductMasterRecord {
  id: string;
  code: string;
  productName: string;
  description: string;
  category: string;
  entities: string[];
  uom: string;
  effectiveFrom: string;
  effectiveTo: string;
  status: 'Active' | 'Inactive';
  glAccount: string;
  taxDetails: TaxDetail[];
  scope: 'BU' | 'Entity' | 'Enterprise';
  createdAt: string;
  createdBy: string;
}

interface TaxDetail {
  id: string;
  taxType: string;
  taxCode: string;
  taxRate: string;
  jurisdiction: string;
}

interface ProductCategory {
  id: string;
  name: string;
  defaultEntities: string[];
  defaultGLAccount: string;
  defaultTaxes: TaxDetail[];
}

interface GLAccount {
  id: string;
  code: string;
  name: string;
  category: string;
}

interface Entity {
  id: string;
  code: string;
  name: string;
  type: 'Entity' | 'Business Unit';
}

interface ColumnDefinition {
  id: string;
  label: string;
  field: string;
  required: boolean;
  defaultVisible: boolean;
  width?: number;
  sortable?: boolean;
}

type ViewMode = 'list' | 'grid' | 'calendar';

// ============================================================================
// SAMPLE DATA
// ============================================================================

const sampleCategories: ProductCategory[] = [
  {
    id: 'cat1',
    name: 'Electronics',
    defaultEntities: ['entity1', 'bu1'],
    defaultGLAccount: 'gl1',
    defaultTaxes: [
      { id: 'tax1', taxType: 'GST', taxCode: 'HSN8471', taxRate: '18%', jurisdiction: 'India' }
    ]
  },
  {
    id: 'cat2',
    name: 'Office Supplies',
    defaultEntities: ['entity1', 'entity2'],
    defaultGLAccount: 'gl2',
    defaultTaxes: [
      { id: 'tax2', taxType: 'GST', taxCode: 'HSN4820', taxRate: '12%', jurisdiction: 'India' }
    ]
  },
  {
    id: 'cat3',
    name: 'Furniture',
    defaultEntities: ['entity1'],
    defaultGLAccount: 'gl3',
    defaultTaxes: [
      { id: 'tax3', taxType: 'VAT', taxCode: 'VAT5', taxRate: '5%', jurisdiction: 'Botswana' }
    ]
  }
];

const sampleEntities: Entity[] = [
  // Entities (50 items)
  { id: 'entity1', code: 'E001', name: 'Sample Entity A', type: 'Entity' },
  { id: 'entity2', code: 'E002', name: 'Sample Entity B', type: 'Entity' },
  { id: 'entity3', code: 'E003', name: 'Test Entity 1', type: 'Entity' },
  { id: 'entity4', code: 'E004', name: 'Alpha Corporation', type: 'Entity' },
  { id: 'entity5', code: 'E005', name: 'Beta Industries Ltd', type: 'Entity' },
  { id: 'entity6', code: 'E006', name: 'Gamma Holdings', type: 'Entity' },
  { id: 'entity7', code: 'E007', name: 'Delta Services Inc', type: 'Entity' },
  { id: 'entity8', code: 'E008', name: 'Epsilon Trading Co', type: 'Entity' },
  { id: 'entity9', code: 'E009', name: 'Zeta Pharmaceuticals', type: 'Entity' },
  { id: 'entity10', code: 'E010', name: 'Eta Technologies', type: 'Entity' },
  { id: 'entity11', code: 'E011', name: 'Theta Financial Group', type: 'Entity' },
  { id: 'entity12', code: 'E012', name: 'Iota Manufacturing', type: 'Entity' },
  { id: 'entity13', code: 'E013', name: 'Kappa Retail Solutions', type: 'Entity' },
  { id: 'entity14', code: 'E014', name: 'Lambda Logistics', type: 'Entity' },
  { id: 'entity15', code: 'E015', name: 'Mu Healthcare Systems', type: 'Entity' },
  { id: 'entity16', code: 'E016', name: 'Nu Energy Corp', type: 'Entity' },
  { id: 'entity17', code: 'E017', name: 'Xi Automotive Parts', type: 'Entity' },
  { id: 'entity18', code: 'E018', name: 'Omicron Foods Ltd', type: 'Entity' },
  { id: 'entity19', code: 'E019', name: 'Pi Software Solutions', type: 'Entity' },
  { id: 'entity20', code: 'E020', name: 'Rho Construction Group', type: 'Entity' },
  { id: 'entity21', code: 'E021', name: 'Sigma Electronics', type: 'Entity' },
  { id: 'entity22', code: 'E022', name: 'Tau Telecommunications', type: 'Entity' },
  { id: 'entity23', code: 'E023', name: 'Upsilon Media Corp', type: 'Entity' },
  { id: 'entity24', code: 'E024', name: 'Phi Aerospace Inc', type: 'Entity' },
  { id: 'entity25', code: 'E025', name: 'Chi Chemical Industries', type: 'Entity' },
  { id: 'entity26', code: 'E026', name: 'Psi Biotech Labs', type: 'Entity' },
  { id: 'entity27', code: 'E027', name: 'Omega Consulting', type: 'Entity' },
  { id: 'entity28', code: 'E028', name: 'Apex Global Trading', type: 'Entity' },
  { id: 'entity29', code: 'E029', name: 'Pinnacle Insurance', type: 'Entity' },
  { id: 'entity30', code: 'E030', name: 'Summit Banking Corp', type: 'Entity' },
  { id: 'entity31', code: 'E031', name: 'Horizon Investments', type: 'Entity' },
  { id: 'entity32', code: 'E032', name: 'Velocity Motors', type: 'Entity' },
  { id: 'entity33', code: 'E033', name: 'Quantum Computing Inc', type: 'Entity' },
  { id: 'entity34', code: 'E034', name: 'Stellar Shipping Co', type: 'Entity' },
  { id: 'entity35', code: 'E035', name: 'Aurora Mining Ltd', type: 'Entity' },
  { id: 'entity36', code: 'E036', name: 'Nexus Real Estate', type: 'Entity' },
  { id: 'entity37', code: 'E037', name: 'Vortex Entertainment', type: 'Entity' },
  { id: 'entity38', code: 'E038', name: 'Eclipse Solar Energy', type: 'Entity' },
  { id: 'entity39', code: 'E039', name: 'Catalyst Ventures', type: 'Entity' },
  { id: 'entity40', code: 'E040', name: 'Fusion Power Systems', type: 'Entity' },
  { id: 'entity41', code: 'E041', name: 'Atlas Heavy Industries', type: 'Entity' },
  { id: 'entity42', code: 'E042', name: 'Titan Steel Works', type: 'Entity' },
  { id: 'entity43', code: 'E043', name: 'Mercury Courier Services', type: 'Entity' },
  { id: 'entity44', code: 'E044', name: 'Neptune Fisheries', type: 'Entity' },
  { id: 'entity45', code: 'E045', name: 'Jupiter Agritech', type: 'Entity' },
  { id: 'entity46', code: 'E046', name: 'Saturn Satellite Systems', type: 'Entity' },
  { id: 'entity47', code: 'E047', name: 'Mars Exploration Corp', type: 'Entity' },
  { id: 'entity48', code: 'E048', name: 'Venus Cosmetics', type: 'Entity' },
  { id: 'entity49', code: 'E049', name: 'Pluto Research Institute', type: 'Entity' },
  { id: 'entity50', code: 'E050', name: 'Orion Defense Systems', type: 'Entity' },
  // Business Units (50 items)
  { id: 'bu1', code: 'BU001', name: 'Sample Business Unit 1', type: 'Business Unit' },
  { id: 'bu2', code: 'BU002', name: 'Sample Business Unit 2', type: 'Business Unit' },
  { id: 'bu3', code: 'BU003', name: 'Test Business Unit 1', type: 'Business Unit' },
  { id: 'bu4', code: 'BU004', name: 'North Region Operations', type: 'Business Unit' },
  { id: 'bu5', code: 'BU005', name: 'South Region Operations', type: 'Business Unit' },
  { id: 'bu6', code: 'BU006', name: 'East Zone Distribution', type: 'Business Unit' },
  { id: 'bu7', code: 'BU007', name: 'West Zone Distribution', type: 'Business Unit' },
  { id: 'bu8', code: 'BU008', name: 'Central Logistics Hub', type: 'Business Unit' },
  { id: 'bu9', code: 'BU009', name: 'Manufacturing Division', type: 'Business Unit' },
  { id: 'bu10', code: 'BU010', name: 'Retail Operations', type: 'Business Unit' },
  { id: 'bu11', code: 'BU011', name: 'Wholesale Division', type: 'Business Unit' },
  { id: 'bu12', code: 'BU012', name: 'E-Commerce Unit', type: 'Business Unit' },
  { id: 'bu13', code: 'BU013', name: 'Asia Pacific Division', type: 'Business Unit' },
  { id: 'bu14', code: 'BU014', name: 'Europe Middle East Africa', type: 'Business Unit' },
  { id: 'bu15', code: 'BU015', name: 'Americas Division', type: 'Business Unit' },
  { id: 'bu16', code: 'BU016', name: 'Research & Development', type: 'Business Unit' },
  { id: 'bu17', code: 'BU017', name: 'Quality Assurance Unit', type: 'Business Unit' },
  { id: 'bu18', code: 'BU018', name: 'Customer Service Center', type: 'Business Unit' },
  { id: 'bu19', code: 'BU019', name: 'Marketing & Sales', type: 'Business Unit' },
  { id: 'bu20', code: 'BU020', name: 'Human Resources Dept', type: 'Business Unit' },
  { id: 'bu21', code: 'BU021', name: 'Finance & Accounting', type: 'Business Unit' },
  { id: 'bu22', code: 'BU022', name: 'IT Services Division', type: 'Business Unit' },
  { id: 'bu23', code: 'BU023', name: 'Legal & Compliance', type: 'Business Unit' },
  { id: 'bu24', code: 'BU024', name: 'Procurement Division', type: 'Business Unit' },
  { id: 'bu25', code: 'BU025', name: 'Supply Chain Management', type: 'Business Unit' },
  { id: 'bu26', code: 'BU026', name: 'Warehouse Operations', type: 'Business Unit' },
  { id: 'bu27', code: 'BU027', name: 'Fleet Management', type: 'Business Unit' },
  { id: 'bu28', code: 'BU028', name: 'Facilities Management', type: 'Business Unit' },
  { id: 'bu29', code: 'BU029', name: 'Security Services', type: 'Business Unit' },
  { id: 'bu30', code: 'BU030', name: 'Training & Development', type: 'Business Unit' },
  { id: 'bu31', code: 'BU031', name: 'Product Development', type: 'Business Unit' },
  { id: 'bu32', code: 'BU032', name: 'Brand Management', type: 'Business Unit' },
  { id: 'bu33', code: 'BU033', name: 'Digital Marketing', type: 'Business Unit' },
  { id: 'bu34', code: 'BU034', name: 'Social Media Unit', type: 'Business Unit' },
  { id: 'bu35', code: 'BU035', name: 'Content Production', type: 'Business Unit' },
  { id: 'bu36', code: 'BU036', name: 'Data Analytics', type: 'Business Unit' },
  { id: 'bu37', code: 'BU037', name: 'Business Intelligence', type: 'Business Unit' },
  { id: 'bu38', code: 'BU038', name: 'Cloud Services', type: 'Business Unit' },
  { id: 'bu39', code: 'BU039', name: 'Cybersecurity Division', type: 'Business Unit' },
  { id: 'bu40', code: 'BU040', name: 'Mobile Development', type: 'Business Unit' },
  { id: 'bu41', code: 'BU041', name: 'Web Development', type: 'Business Unit' },
  { id: 'bu42', code: 'BU042', name: 'API Integration Team', type: 'Business Unit' },
  { id: 'bu43', code: 'BU043', name: 'Infrastructure Services', type: 'Business Unit' },
  { id: 'bu44', code: 'BU044', name: 'Network Operations', type: 'Business Unit' },
  { id: 'bu45', code: 'BU045', name: 'Help Desk Support', type: 'Business Unit' },
  { id: 'bu46', code: 'BU046', name: 'Project Management Office', type: 'Business Unit' },
  { id: 'bu47', code: 'BU047', name: 'Strategic Planning', type: 'Business Unit' },
  { id: 'bu48', code: 'BU048', name: 'Investor Relations', type: 'Business Unit' },
  { id: 'bu49', code: 'BU049', name: 'Corporate Communications', type: 'Business Unit' },
  { id: 'bu50', code: 'BU050', name: 'Executive Office', type: 'Business Unit' }
];

const sampleGLAccounts: GLAccount[] = [
  { id: 'gl1', code: '5001', name: 'Electronics Inventory', category: 'Inventory' },
  { id: 'gl2', code: '5002', name: 'Office Supplies Inventory', category: 'Inventory' },
  { id: 'gl3', code: '5003', name: 'Furniture Inventory', category: 'Inventory' },
  { id: 'gl4', code: '5004', name: 'Raw Materials', category: 'Inventory' },
  { id: 'gl5', code: '5005', name: 'Finished Goods', category: 'Inventory' }
];

const sampleUOMs: string[] = ['Piece', 'Box', 'Carton', 'Kg', 'Liter', 'Meter', 'Set'];

const sampleTaxProducts: TaxDetail[] = [
  { id: 'tax1', taxType: 'GST', taxCode: 'HSN8471', taxRate: '18%', jurisdiction: 'India' },
  { id: 'tax2', taxType: 'GST', taxCode: 'HSN4820', taxRate: '12%', jurisdiction: 'India' },
  { id: 'tax3', taxType: 'GST', taxCode: 'HSN9403', taxRate: '18%', jurisdiction: 'India' },
  { id: 'tax4', taxType: 'VAT', taxCode: 'VAT5', taxRate: '5%', jurisdiction: 'Botswana' }
];

// NOTE: Sample data removed. All list data now comes from the API via useSampleServices hook.
// The backend handles search (matching ALL text columns in the grid) and pagination server-side.

// ============================================================================
// COLUMN DEFINITIONS (v2.0 - NEW)
// ============================================================================

const columnDefinitions: ColumnDefinition[] = [
  {
    id: 'select',
    label: '',
    field: 'select',
    required: true,
    defaultVisible: true,
    width: 50,
    sortable: false
  },
  {
    id: 'code',
    label: 'Code',
    field: 'code',
    required: false,
    defaultVisible: true,
    sortable: true
  },
  {
    id: 'productName',
    label: 'Product Name',
    field: 'productName',
    required: false,
    defaultVisible: true,
    sortable: true
  },
  {
    id: 'description',
    label: 'Description',
    field: 'description',
    required: false,
    defaultVisible: true,
    sortable: false
  },
  {
    id: 'category',
    label: 'Category',
    field: 'category',
    required: false,
    defaultVisible: true,
    sortable: true
  },
  {
    id: 'uom',
    label: 'UOM',
    field: 'uom',
    required: false,
    defaultVisible: true,
    sortable: true
  },
  {
    id: 'status',
    label: 'Status',
    field: 'status',
    required: false,
    defaultVisible: true,
    sortable: true
  },
  {
    id: 'entities',
    label: 'Entities',
    field: 'entities',
    required: false,
    defaultVisible: false,
    sortable: false
  },
  {
    id: 'effectiveFrom',
    label: 'Effective From',
    field: 'effectiveFrom',
    required: false,
    defaultVisible: false,
    sortable: true
  },
  {
    id: 'effectiveTo',
    label: 'Effective To',
    field: 'effectiveTo',
    required: false,
    defaultVisible: false,
    sortable: true
  },
  {
    id: 'glAccount',
    label: 'GL Account',
    field: 'glAccount',
    required: false,
    defaultVisible: false,
    sortable: true
  },
  {
    id: 'actions',
    label: 'Actions',
    field: 'actions',
    required: true,
    defaultVisible: true,
    width: 80,
    sortable: false
  }
];

// ============================================================================
// COLUMN SELECTOR COMPONENT (v2.0 - NEW)
// ============================================================================

interface ColumnSelectorProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onApply: (visibleColumns: string[]) => void;
  onReset: () => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleColumns: initialVisible,
  onApply,
  onReset
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialVisible);
  const [isMobile, setIsMobile] = useState(false);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedColumns(initialVisible);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, initialVisible]);

  const toggleColumn = (columnId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (column?.required) return;

    setSelectedColumns(prev =>
      prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  const handleSelectAll = () => {
    const allColumnIds = columns
      .filter(col => !col.required)
      .map(col => col.id);

    const requiredIds = columns
      .filter(col => col.required)
      .map(col => col.id);

    if (selectedColumns.length === columns.length) {
      setSelectedColumns(requiredIds);
    } else {
      setSelectedColumns([...requiredIds, ...allColumnIds]);
    }
  };

  const handleReset = () => {
    setConfirmResetOpen(true);
  };

  const handleConfirmReset = () => {
    onReset();
    setConfirmResetOpen(false);
    setIsOpen(false);
  };

  const handleApply = () => {
    onApply(selectedColumns);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setSelectedColumns(initialVisible);
    setIsOpen(false);
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={panelRef}>
      <button
        className={`btn-icon ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Manage Columns"
        style={isOpen ? {
          background: 'var(--accent-gradient)',
          color: 'white',
          borderColor: 'var(--primary)'
        } : {}}
      >
        <IconSystem.grid size={18} />
      </button>

      {isOpen && (
        <>
          {isMobile && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                zIndex: 999
              }}
              onClick={() => setIsOpen(false)}
            />
          )}

          <div style={{
            position: isMobile ? 'fixed' : 'absolute',
            top: isMobile ? '10%' : 'calc(100% + 0.5rem)',
            left: isMobile ? '50%' : 'auto',
            right: isMobile ? 'auto' : 0,
            transform: isMobile ? 'translateX(-50%)' : 'none',
            width: isMobile ? 'calc(100vw - 2rem)' : '320px',
            maxWidth: '320px',
            maxHeight: isMobile ? 'calc(90vh - 2rem)' : '500px',
            background: 'var(--bg-primary)',
            border: '2px solid var(--primary)',
            borderRadius: '0.75rem',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            animation: 'slideDown 0.2s ease',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
          <div style={{
            padding: '1rem 1.25rem',
            borderBottom: '2px solid var(--border-color)',
            background: 'var(--bg-secondary)',
            borderRadius: '0.75rem 0.75rem 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0
          }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
              Manage Columns
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                width: '1.5rem',
                height: '1.5rem',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '0.25rem'
              }}
            >
              <IconSystem.x size={16} />
            </button>
          </div>

          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '0.75rem 0',
            minHeight: 0
          }}>
            {columns.map(column => (
              <div
                key={column.id}
                onClick={() => toggleColumn(column.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.625rem 1.25rem',
                  cursor: column.required ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  gap: '0.75rem',
                  opacity: column.required ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!column.required) {
                    e.currentTarget.style.background = '#f8fafc';
                    e.currentTarget.style.paddingLeft = '1.5rem';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.paddingLeft = '1.25rem';
                }}
              >
                <div style={{
                  width: '1.125rem',
                  height: '1.125rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid var(--border-color)',
                  borderRadius: '0.25rem',
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                  ...(selectedColumns.includes(column.id) ? {
                    background: 'var(--accent-gradient)',
                    borderColor: 'var(--primary)',
                    color: 'white'
                  } : {})
                }}>
                  {selectedColumns.includes(column.id) && <IconSystem.check size={14} />}
                </div>
                <span style={{
                  flex: 1,
                  fontSize: '0.85rem',
                  color: 'var(--text-primary)',
                  userSelect: 'none'
                }}>
                  {column.label}{column.required && ' *'}
                </span>
              </div>
            ))}
          </div>

          <div style={{
            padding: '1rem',
            borderTop: '2px solid var(--border-color)',
            background: 'var(--bg-primary)',
            boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleSelectAll}
                style={{
                  flex: 1,
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.75rem',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.5rem',
                  cursor: 'pointer'
                }}
              >
                {selectedColumns.length === columns.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={handleReset}
                style={{
                  flex: 1,
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.75rem',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.5rem',
                  cursor: 'pointer'
                }}
              >
                Reset Default
              </button>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCancel}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                className="btn btn-primary"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </>
      )}

      <ConfirmDialog
        open={confirmResetOpen}
        title="Reset Columns"
        message="Reset to default column configuration?"
        confirmLabel="Reset"
        cancelLabel="Cancel"
        onConfirm={handleConfirmReset}
        onCancel={() => setConfirmResetOpen(false)}
      />
    </div>
  );
};

// ============================================================================
// GRID VIEW CARD COMPONENT (v2.0 - NEW)
// ============================================================================

interface ProductCardProps {
  product: ProductMasterRecord;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onView, onEdit, onDelete }) => {
  const glAccount = sampleGLAccounts.find(gl => gl.id === product.glAccount);
  const primaryTax = product.taxDetails[0];

  const menuItems: ActionMenuItem[] = [
    {
      id: 'view',
      label: 'View Details',
      icon: <IconSystem.eye size={18} />,
      onClick: () => onView(product.id),
    },
    {
      id: 'edit',
      label: 'Edit',
      icon: <IconSystem.edit size={18} />,
      onClick: () => onEdit(product.id),
    },
    {
      id: 'copy',
      label: 'Copy',
      icon: <IconSystem.copy size={18} />,
      onClick: () => onEdit(product.id),
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: <IconSystem.trash size={18} />,
      onClick: () => onDelete(product.id),
      variant: 'danger',
    },
  ];

  return (
    <div style={{
      background: 'var(--bg-primary)',
      border: '1px solid var(--border-color)',
      borderRadius: '0.75rem',
      padding: '1.25rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.2s ease',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem'
    }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'var(--text-secondary)',
            marginBottom: '0.25rem'
          }}>
            {product.code}
          </div>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: 700,
            color: 'var(--text-primary)',
            margin: 0,
            marginBottom: '0.5rem'
          }}>
            {product.productName}
          </h3>
        </div>
        <ActionMenu items={menuItems} />
      </div>

      {product.description && (
        <p style={{
          fontSize: '0.85rem',
          color: 'var(--text-secondary)',
          margin: 0,
          lineHeight: 1.5,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {product.description}
        </p>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid rgba(226, 232, 240, 0.5)'
      }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Category</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>{product.category}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>UOM</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>{product.uom}</div>
        </div>
        {primaryTax && (
          <div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Tax</div>
            <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>
              {primaryTax.taxCode} - {primaryTax.taxRate}
            </div>
          </div>
        )}
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Status</div>
          <span className={`badge badge-${product.status.toLowerCase()}`}>
            {product.status}
          </span>
        </div>
      </div>

      {glAccount && (
        <div style={{
          paddingTop: '0.75rem',
          borderTop: '1px solid rgba(226, 232, 240, 0.5)',
          fontSize: '0.75rem',
          color: '#64748b'
        }}>
          <strong>GL Account:</strong> {glAccount.code} - {glAccount.name}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN PRODUCT MASTER SCREEN COMPONENT
// ============================================================================

interface SampleMasterScreenProps {
  /** Icon name from the navigation menu item (e.g. 'box', 'user').
   *  Passed automatically by the layout via moduleIcon prop. */
  moduleIcon?: string;
  /** Tab ID — passed by layout for minimize-to-bar support */
  tabId?: string;
  /** Module name — passed by layout for minimize-to-bar support */
  moduleName?: string;
}

const toServiceRecord = (p: ProductMasterRecord): ServiceMasterRecord => ({
  id: p.id,
  code: p.code,
  serviceName: p.productName,
  description: p.description,
  category: p.category,
  entities: p.entities,
  uom: p.uom,
  effectiveFrom: p.effectiveFrom,
  effectiveTo: p.effectiveTo,
  status: p.status,
  glAccount: p.glAccount,
  taxDetails: p.taxDetails,
  scope: p.scope,
  createdAt: p.createdAt,
  createdBy: p.createdBy,
});

const SampleMasterScreen: React.FC<SampleMasterScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const defaultVisible = columnDefinitions
      .filter(col => col.defaultVisible)
      .map(col => col.id);
    return defaultVisible;
  });

  // ---- Server-side pagination & search state ----
  const [queryParams, setQueryParams] = useState<SampleServiceSearchRequest>({
    page: 1,
    pageSize: 25,
  });
  const { data: apiData, loading, pagination: paginationMeta, refetch } = useSampleServices(queryParams);

  // Map API response (SampleServiceListItem) to local grid type (ProductMasterRecord)
  const products: ProductMasterRecord[] = useMemo(() =>
    apiData.map(item => ({
      id: item.id as string,
      code: item.code,
      productName: item.serviceName,
      description: item.description,
      category: item.category,
      entities: item.entities,
      uom: item.uom,
      effectiveFrom: item.effectiveFrom,
      effectiveTo: item.effectiveTo,
      status: item.status as 'Active' | 'Inactive',
      glAccount: item.glAccount,
      taxDetails: [],
      scope: 'Entity' as const,
      createdAt: '',
      createdBy: '',
    })),
    [apiData]
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [codeFilter, setCodeFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [descriptionFilter, setDescriptionFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [uomFilter, setUomFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState<string[]>([]);
  const [effectiveFromFilter, setEffectiveFromFilter] = useState('');
  const [effectiveToFilter, setEffectiveToFilter] = useState('');
  const [glAccountFilter, setGlAccountFilter] = useState('');
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchPopupOpen, setSearchPopupOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductMasterRecord | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const copyInstanceCounter = useRef(0);
  const [copyInstanceId, setCopyInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  // ---- Search debounce (server-side) ----
  // Debounces the search input and sends searchTerm to the API.
  // The backend should match searchTerm against ALL text columns displayed in the grid
  // (e.g. code, productName, description, category, uom, status, glAccount, etc.)
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams(prev => ({
        ...prev,
        search: searchQuery || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ---- Server-side pagination handlers ----
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 })); // API uses 1-based
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const handleApplyColumns = useCallback((newVisibleColumns: string[]) => {
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('product-master-columns', JSON.stringify(newVisibleColumns));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaultVisible = columnDefinitions
      .filter(col => col.defaultVisible)
      .map(col => col.id);
    setVisibleColumns(defaultVisible);
    localStorage.removeItem('product-master-columns');
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('product-master-columns');
    if (saved) {
      try {
        setVisibleColumns(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load column preferences');
      }
    }
  }, []);

  const handleRestoreModal = useCallback((restoredMode: 'create' | 'edit' | 'copy', instanceId?: string, serviceCode?: string) => {
    if (instanceId) {
      if (restoredMode === 'copy') setCopyInstanceId(instanceId);
      else setCreateInstanceId(instanceId);
    }
    if (serviceCode) {
      const product = products.find(p => p.code === serviceCode);
      if (product) setSelectedProduct(product);
    }
    setFormMode(restoredMode);
    setFormModalOpen(true);
  }, [products]);

  // F2 keyboard shortcut to open search popup
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2' && !searchPopupOpen && !formModalOpen && !viewModalOpen && !deleteModalOpen) {
        e.preventDefault();
        setSearchPopupOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchPopupOpen, formModalOpen, viewModalOpen, deleteModalOpen]);

  const handleView = useCallback((id: string) => {
    const product = products.find(s => s.id === id);
    if (!product) return;
    const viewId = tabId ? `${tabId}-view-modal-${product.code}` : null;
    // If this exact record's view is already minimized, restore it
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId);
      modal?.onRestore?.();
      return;
    }
    setSelectedProduct(product);
    setViewModalOpen(true);
  }, [products, tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleRestoreView = useCallback((serviceCode: string) => {
    const product = products.find(p => p.code === serviceCode);
    if (product) {
      setSelectedProduct(product);
      setViewModalOpen(true);
    }
  }, [products]);

  const handleEdit = useCallback((id: string) => {
    const product = products.find(s => s.id === id);
    if (!product) return;
    const modalId = tabId ? `${tabId}-form-modal-edit-${product.code}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setFormMode('edit');
    setSelectedProduct(product);
    setFormModalOpen(true);
  }, [products, tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleCopy = useCallback((id: string) => {
    const product = products.find(s => s.id === id);
    if (!product) return;
    if (formModalOpen) {
      formModalMinimizeRef.current?.();
    }
    const newId = String(++copyInstanceCounter.current);
    setCopyInstanceId(newId);
    setFormMode('copy');
    setSelectedProduct(product);
    setFormModalOpen(true);
  }, [products, formModalOpen]);

  const handleDelete = useCallback((id: string) => {
    const product = products.find(s => s.id === id);
    setSelectedProduct(product || null);
    setDeleteModalOpen(true);
  }, [products]);

  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setFormMode('create');
    setSelectedProduct(null);
    setFormModalOpen(true);
  }, []);


  const handleSaveForm = useCallback(() => {
    // In production: API call to save then refresh list
    // Modal handles validation, loading overlay, result dialog, and close
    console.log('Product saved');
  }, []);

  // SyutGrid column definitions
  const tableColumns: SyutGridColumn<ProductMasterRecord>[] = useMemo(() => [
    { id: 'code', header: 'Code', accessorKey: 'code', size: 120 },
    { id: 'productName', header: 'Product Name', accessorKey: 'productName', size: 200 },
    { id: 'description', header: 'Description', accessorKey: 'description', size: 200, enableSorting: false },
    { id: 'category', header: 'Category', accessorKey: 'category', size: 180 },
    { id: 'uom', header: 'UOM', accessorKey: 'uom', size: 100 },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      size: 100,
      cell: ({ getValue }) => (
        <span className={`badge badge-${(getValue() as string).toLowerCase()}`}>
          {getValue() as string}
        </span>
      ),
    },
    {
      id: 'entities',
      header: 'Entities',
      accessorKey: 'entities',
      size: 200,
      enableSorting: false,
      cell: ({ getValue }) => {
        const entities = getValue() as string[];
        return entities.map(eid => {
          const entity = sampleEntities.find(e => e.id === eid);
          return entity?.name || eid;
        }).join(', ');
      },
    },
    { id: 'effectiveFrom', header: 'Effective From', accessorKey: 'effectiveFrom', size: 130 },
    {
      id: 'effectiveTo',
      header: 'Effective To',
      accessorKey: 'effectiveTo',
      size: 130,
      cell: ({ getValue }) => (getValue() as string) || '-',
    },
    {
      id: 'glAccount',
      header: 'GL Account',
      accessorKey: 'glAccount',
      size: 200,
      cell: ({ getValue }) => {
        const glAccount = sampleGLAccounts.find(gl => gl.id === getValue());
        return glAccount ? `${glAccount.code} - ${glAccount.name}` : '-';
      },
    },
  ], []);

  // Row actions for SyutGrid
  const getRowActions = useCallback((product: ProductMasterRecord): ActionMenuItem[] => [
    {
      id: 'view',
      label: 'View Details',
      icon: <IconSystem.eye size={18} />,
      onClick: () => handleView(product.id),
    },
    {
      id: 'edit',
      label: 'Edit',
      icon: <IconSystem.edit size={18} />,
      onClick: () => handleEdit(product.id),
    },
    {
      id: 'copy',
      label: 'Copy',
      icon: <IconSystem.copy size={18} />,
      onClick: () => handleCopy(product.id),
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: <IconSystem.trash size={18} />,
      onClick: () => handleDelete(product.id),
      variant: 'danger',
    },
  ], [handleView, handleEdit, handleCopy, handleDelete]);

  const viewModeOptions: ViewModeOption[] = [
    {
      id: 'list',
      icon: <IconSystem.list size={16} />,
      label: 'List View',
      tooltip: 'List View'
    },
    {
      id: 'grid',
      icon: <IconSystem.grid size={16} />,
      label: 'Grid View',
      tooltip: 'Grid View'
    },
    {
      id: 'calendar',
      icon: <IconSystem.calendar size={16} />,
      label: 'Calendar View',
      tooltip: 'Calendar View (Not Applicable)',
      disabled: true
    }
  ];

  const handleTestNotifications = useCallback(() => {
    notificationService.success('Product Added', 'New product has been added successfully');

    setTimeout(() => {
      notificationService.info('Stock Alert', 'Low stock warning for 5 products');
    }, 1000);

    setTimeout(() => {
      notificationService.warning('Price Update', 'Bulk price update pending approval');
    }, 2000);

    setTimeout(() => {
      notificationService.error('Import Failed', 'Could not import product catalog');
    }, 3000);
  }, []);

  const actionButtons: ActionButton[] = [
    {
      id: 'search',
      icon: <IconSystem.search size={18} />,
      label: 'Search Products',
      onClick: () => setSearchPopupOpen(true),
      tooltip: 'Search Products (F2)',
      variant: 'icon',
      visible: true
    },
    {
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'Add New Product',
      onClick: handleAddNew,
      tooltip: 'Add New Product',
      variant: 'icon',
      visible: true
    },
    {
      id: 'test-notifications',
      icon: <IconSystem.bell size={18} />,
      label: 'Test Notifications',
      onClick: handleTestNotifications,
      tooltip: 'Test Notifications',
      variant: 'icon'
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => {},
      tooltip: 'Refresh',
      variant: 'icon'
    },
    {
      id: 'export',
      icon: <IconSystem.download size={18} />,
      label: 'Export',
      onClick: () => console.log('Export clicked'),
      tooltip: 'Export',
      variant: 'icon'
    }
  ];

  // Convert ProductMasterRecord to the format expected by modals
  const selectedServiceForModal = selectedProduct ? {
    id: selectedProduct.id,
    code: selectedProduct.code,
    serviceName: selectedProduct.productName,
    description: selectedProduct.description,
    category: selectedProduct.category,
    entities: selectedProduct.entities,
    uom: selectedProduct.uom,
    effectiveFrom: selectedProduct.effectiveFrom,
    effectiveTo: selectedProduct.effectiveTo,
    status: selectedProduct.status,
    glAccount: selectedProduct.glAccount,
    taxDetails: selectedProduct.taxDetails,
    scope: selectedProduct.scope,
    createdAt: selectedProduct.createdAt,
    createdBy: selectedProduct.createdBy,
  } : null;

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Product Master Definition"
        description="Catalogue of all products managed by the enterprise"
        icon={moduleIcon && (IconSystem as any)[moduleIcon]
          ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
          : undefined
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search products...'
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode as ViewMode)
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          {viewMode === 'list' && (
            <SyutGrid
              data={products}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              manualPagination
              totalCount={paginationMeta.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              rowActions={getRowActions}
              emptyMessage="No products found"
              frozenLeftColumns={['select', 'code']}
              frozenRightColumns={['actions']}
              visibleColumns={visibleColumns}
            />
          )}

          {viewMode === 'grid' && (() => {
            const currentPage = queryParams.page ?? 1;
            const currentPageSize = queryParams.pageSize ?? 25;
            const totalPages = Math.max(1, Math.ceil(paginationMeta.totalItems / currentPageSize));
            return (
              <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{
                  padding: '1rem',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: '1.5rem',
                  flex: 1,
                  overflow: 'auto',
                  alignContent: 'start',
                }}>
                  {products.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      <IconSystem.grid size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                      <p>No products found</p>
                    </div>
                  ) : (
                    products.map((product: ProductMasterRecord) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        onView={handleView}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                      />
                    ))
                  )}
                </div>
                {products.length > 0 && (
                  <div className="pagination-footer">
                    <div className="pagination-info">
                      Showing {products.length} of {paginationMeta.totalItems} items
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select
                        value={currentPageSize}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '1rem' }}
                      >
                        {[10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </select>
                      <button className="page-btn" onClick={() => handlePageChange(0)} disabled={currentPage <= 1}>
                        <IconSystem.chevronLeft size={16} /><IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(currentPage - 2)} disabled={currentPage <= 1}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.75rem', fontSize: '0.85rem' }}>
                        Page {currentPage} of {totalPages}
                      </span>
                      <button className="page-btn" onClick={() => handlePageChange(currentPage)} disabled={currentPage >= totalPages}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage >= totalPages}>
                        <IconSystem.chevronRight size={16} /><IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {viewMode === 'calendar' && (
            <div style={{
              padding: '3rem',
              textAlign: 'center',
              color: 'var(--text-muted)'
            }}>
              <IconSystem.calendar size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
              <h3 style={{ color: '#64748b', marginBottom: '0.5rem' }}>Calendar View Not Applicable</h3>
              <p>Calendar view is not available for Product Master</p>
            </div>
          )}
        </div>

        {filterOpen && <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button
              onClick={() => { setCodeFilter(''); setNameFilter(''); setDescriptionFilter(''); setCategoryFilter(''); setUomFilter(''); setStatusFilter(''); setEntityFilter([]); setEffectiveFromFilter(''); setEffectiveToFilter(''); setGlAccountFilter(''); setFilterOpen(false); }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                color: 'var(--text-muted)',
                borderRadius: '0.25rem',
              }}
              title="Close filters"
            >
              <IconSystem.x size={16} />
            </button>
          </div>

          <div className="search-panel-content">
            {/* Code Filter */}
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Code</label>
              <input
                type="text"
                className="form-input"
                value={codeFilter}
                onChange={(e) => setCodeFilter(e.target.value)}
                placeholder="Filter by code..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            {/* Product Name Filter */}
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Product Name</label>
              <input
                type="text"
                className="form-input"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Filter by name..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            {/* Description Filter */}
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Description</label>
              <input
                type="text"
                className="form-input"
                value={descriptionFilter}
                onChange={(e) => setDescriptionFilter(e.target.value)}
                placeholder="Filter by description..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            {/* Category Filter */}
            <div className="filter-section">
              <SyutSelect
                label="Category"
                options={[
                  { value: '', label: 'All Categories' },
                  ...sampleCategories.map(c => ({ value: c.name, label: c.name }))
                ]}
                value={categoryFilter}
                onChange={(val: string | string[] | null) => setCategoryFilter((val as string) || '')}
                placeholder="Search categories..."
                isClearable={false}
                size="sm"
              />
            </div>

            {/* UOM Filter */}
            <div className="filter-section">
              <SyutSelect
                label="UOM"
                options={[
                  { value: '', label: 'All UOMs' },
                  ...sampleUOMs.map(u => ({ value: u, label: u }))
                ]}
                value={uomFilter}
                onChange={(val: string | string[] | null) => setUomFilter((val as string) || '')}
                placeholder="Select UOM..."
                isClearable={false}
                size="sm"
              />
            </div>

            {/* Status Filter */}
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'Active', label: 'Active' },
                  { value: 'Inactive', label: 'Inactive' }
                ]}
                value={statusFilter}
                onChange={(val: string | string[] | null) => setStatusFilter((val as string) || '')}
                placeholder="Search status..."
                isSearchable={true}
                isClearable={false}
                size="sm"
              />
            </div>

            {/* BU / Entity Filter */}
            <div className="filter-section">
              <SyutSelect
                label="BU / Entity (Async)"
                value={entityFilter}
                onChange={(val: string | string[] | null) => setEntityFilter((val as string[]) || [])}
                placeholder="None selected"
                searchPlaceholder="Search entities..."
                isMulti
                variant="checkbox"
                showSelectAll
                selectAllLabel="Select All"
                size="sm"
                isAsync
                loadOptions={async (inputValue: string) => {
                  // Simulate API call with delay
                  await new Promise(resolve => setTimeout(resolve, 500));

                  // Filter entities based on search query (simulating server-side search)
                  const filtered = sampleEntities.filter(e =>
                    e.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                    e.code.toLowerCase().includes(inputValue.toLowerCase())
                  );

                  return filtered.map(e => ({
                    value: e.id,
                    label: `${e.code} - ${e.name}`
                  }));
                }}
                defaultOptions={sampleEntities.slice(0, 10).map(e => ({
                  value: e.id,
                  label: `${e.code} - ${e.name}`
                }))}
                debounceDelay={300}
                minSearchLength={1}
                loadingMessage="Searching entities..."
                minSearchMessage="Type to search entities..."
                cacheOptions
              />
            </div>

            {/* Effective From Filter */}
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Effective From</label>
              <input
                type="date"
                className="form-input"
                value={effectiveFromFilter}
                onChange={(e) => setEffectiveFromFilter(e.target.value)}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            {/* Effective To Filter */}
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Effective To</label>
              <input
                type="date"
                className="form-input"
                value={effectiveToFilter}
                onChange={(e) => setEffectiveToFilter(e.target.value)}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            {/* GL Account Filter */}
            <div className="filter-section">
              <SyutSelect
                label="GL Account"
                options={[
                  { value: '', label: 'All GL Accounts' },
                  ...sampleGLAccounts.map(gl => ({ value: gl.id, label: `${gl.code} - ${gl.name}` }))
                ]}
                value={glAccountFilter}
                onChange={(val: string | string[] | null) => setGlAccountFilter((val as string) || '')}
                placeholder="Select GL account..."
                isClearable={false}
                size="sm"
              />
            </div>
          </div>

          <div className="filter-actions">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setCodeFilter('');
                setNameFilter('');
                setDescriptionFilter('');
                setCategoryFilter('');
                setUomFilter('');
                setStatusFilter('');
                setEntityFilter([]);
                setEffectiveFromFilter('');
                setEffectiveToFilter('');
                setGlAccountFilter('');
                setQueryParams(prev => ({ ...prev, categoryId: undefined, entityId: undefined, status: undefined, scope: undefined, effectiveFrom: undefined, effectiveTo: undefined, page: 1 }));
              }}
            >
              Clear
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                // Push filter values to queryParams for server-side filtering
                setQueryParams(prev => ({
                  ...prev,
                  categoryId: categoryFilter || undefined,
                  entityId: entityFilter.length > 0 ? entityFilter[0] : undefined,
                  status: statusFilter ? (statusFilter as any) : undefined,
                  effectiveFrom: effectiveFromFilter || undefined,
                  effectiveTo: effectiveToFilter || undefined,
                  page: 1,
                }));
              }}
            >
              Apply
            </button>
          </div>
        </div>}
      </div>

      <SampleMasterFormModal
        isOpen={formModalOpen}
        mode={formMode}
        service={selectedProduct ? toServiceRecord(selectedProduct) : null}
        onSave={handleSaveForm}
        onClose={() => setFormModalOpen(false)}
        categories={sampleCategories}
        entities={sampleEntities}
        glAccounts={sampleGLAccounts}
        uoms={sampleUOMs}
        taxServices={sampleTaxProducts}
        existingServices={products.map(toServiceRecord)}
        openedBy="ProductMasterScreen"
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        copyInstanceId={copyInstanceId}
        minimizeRef={formModalMinimizeRef}
      />

      <ServiceMasterViewModal
        isOpen={viewModalOpen}
        service={selectedServiceForModal}
        onClose={() => setViewModalOpen(false)}
        onEdit={(service) => handleEdit(service.id)}
        glAccounts={sampleGLAccounts}
        entities={sampleEntities}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreView={handleRestoreView}
      />

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedServiceForModal?.serviceName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={() => {
          setIsDeleting(true);
          setTimeout(() => {
            try {
              if (selectedProduct) {
                console.log('Delete:', selectedProduct.id);
                // In production: API call to delete
              }
              setIsDeleting(false);
              setDeleteModalOpen(false);
              notificationService.toast.success(`"${selectedServiceForModal?.serviceName}" has been deleted successfully.`);
              setSelectedProduct(null);
            } catch {
              setIsDeleting(false);
              notificationService.toast.error('An error occurred while deleting. Please try again.');
            }
          }, 800);
        }}
        onCancel={() => setDeleteModalOpen(false)}
      />

      {/* Product Search Popup */}
      <SyutSearchGrid<ProductMasterRecord>
        isOpen={searchPopupOpen}
        onClose={() => setSearchPopupOpen(false)}
        onSelect={(product) => {
          console.log('Selected product:', product);
          handleView(product.id);
        }}
        title="Search Products"
        columns={[
          { key: 'code', header: 'Code', width: '100px', sortable: true },
          { key: 'productName', header: 'Product Name', width: '200px', sortable: true },
          { key: 'category', header: 'Category', width: '150px', sortable: true },
          { key: 'uom', header: 'UOM', width: '80px', sortable: true },
          {
            key: 'status',
            header: 'Status',
            width: '100px',
            render: (value: string) => (
              <span style={{
                padding: '0.125rem 0.5rem',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: 500,
                background: value === 'Active' ? '#dcfce7' : '#fee2e2',
                color: value === 'Active' ? '#166534' : '#991b1b',
              }}>
                {value}
              </span>
            )
          },
        ]}
        data={products}
        filters={[
          {
            key: 'category',
            label: 'Category',
            type: 'select',
            options: sampleCategories.map(c => ({ value: c.name, label: c.name }))
          },
          {
            key: 'status',
            label: 'Status',
            type: 'select',
            options: [
              { value: 'Active', label: 'Active' },
              { value: 'Inactive', label: 'Inactive' }
            ]
          }
        ]}
        rowKey="id"
        enableRecentlyUsed
        recentlyUsedKey="product-master-search"
        searchPlaceholder="Search by code or name... (F2 to focus)"
      />

    </div>
  );
};

export default SampleMasterScreen;
